import { useEffect, useMemo } from "react"
import {
  Box,
  BoxProps,
  ButtonProps,
  Flex,
  FlexProps,
  Input,
  InputProps,
  Spinner,
  Text,
  TextProps,
  useMultiStyleConfig,
  useNumberInput,
} from "@chakra-ui/react"

import {
  selectHasLoadedWalletBalances,
  selectIsConnected,
  useDappSelector,
} from "#/redux-state"
import TokenAmountInputMaxButton from "#/shared/components/TokenAmountInput//TokenAmountInputMaxButton"
import TokenAmountInputTokenLabel from "#/shared/components/TokenAmountInput/TokenAmounInputTokenLabel"
import { useTokenAmountInput } from "#/shared/hooks"

export type TokenAmountInputProps = InputProps & {
  tokenAmount: string
  setTokenAmountCallback: (amount: string) => void
  /**
   * Minimum value that will pass validation. In token (user) unit.
   */
  minTokenAmount?: string
  /**
   * Maximum value that will fail validation after that. In token (user) unit.
   */
  maxTokenAmount?: string
  additionalValidationCallback?: (
    input: string,
    min?: string,
    max?: string,
  ) => {
    isValid: boolean
    errorMessage: string | JSX.Element
  }
  errorCallback?: (
    hasError: boolean,
    errorMessage: string | JSX.Element,
  ) => void
  tokenDecimals: number
  withMaxButton?: boolean
  maxButtonText?: string
  maxButtonProps?: ButtonProps
  tokenLabel?: string
  tokenLabelProps?: TextProps
  tokenLabelTextOffset?: number
  defaultLabelPosition?: number
  placeholder: string
  precisionAmount: number
  readOnly?: boolean
  errorMessageProps?: TextProps
  inputDescription?: JSX.Element
  checkWalletBalances?: boolean
  customMessageContent?: string
}

export default function TokenAmountInput({
  tokenAmount,
  setTokenAmountCallback,
  minTokenAmount = "0",
  maxTokenAmount,
  additionalValidationCallback,
  errorCallback,
  tokenDecimals,
  withMaxButton = false,
  maxButtonText = "MAX",
  maxButtonProps,
  tokenLabel,
  tokenLabelProps,
  tokenLabelTextOffset,
  defaultLabelPosition,
  placeholder,
  precisionAmount,
  readOnly,
  variant,
  size,
  errorMessageProps,
  inputDescription,
  checkWalletBalances = true,
  pattern,
  customMessageContent,
  ...props
}: TokenAmountInputProps) {
  const hasLoadedWalletBalances = useDappSelector(selectHasLoadedWalletBalances)
  const isConnected = useDappSelector(selectIsConnected)

  const styles = useMultiStyleConfig("TokenAmountInput", { variant, size })

  const tokenAmountWithoutCommas = useMemo(
    () => tokenAmount.replace(/,/g, ""),
    [tokenAmount],
  )

  const { handleTokenAmountChange, showError, errorMessage } =
    useTokenAmountInput(
      tokenAmountWithoutCommas,
      setTokenAmountCallback,
      minTokenAmount,
      maxTokenAmount,
      tokenDecimals,
      precisionAmount,
      additionalValidationCallback,
    )

  const { getInputProps } = useNumberInput({
    value: tokenAmount,
    onChange: handleTokenAmountChange,
    pattern,
  })

  useEffect(() => {
    if (errorCallback) errorCallback(showError, errorMessage ?? "")
  }, [errorCallback, showError, errorMessage])

  const inputStyles = {
    ...(styles.input as InputProps),
    borderColor: showError ? "#EF7751" : "primary.black.70",
    _focus: {
      borderColor: showError ? "#EF7751" : "primary.black.70",
    },
    paddingRight:
      // eslint-disable-next-line no-nested-ternary
      tokenLabel && withMaxButton
        ? "220px"
        : tokenLabel || withMaxButton
          ? "165px"
          : "0px",
    ...props,
  }

  const errorMessageContent =
    typeof errorMessage === "object" ? (
      <Box {...(styles.errorMessage as BoxProps)}>{errorMessage}</Box>
    ) : (
      <Text {...errorMessageProps} {...(styles.errorMessage as TextProps)}>
        {errorMessage}
      </Text>
    )

  const messageContent =
    customMessageContent ||
    (isConnected && !hasLoadedWalletBalances && checkWalletBalances) ? (
      <Flex
        {...errorMessageProps}
        {...(styles.errorMessage as FlexProps)}
        alignItems="center"
        gap={1}
      >
        <Spinner size="xs" color="primary.black.100" />
        <Text color="primary.black.100" variant="label" lineHeight="14px">
          {customMessageContent ?? "Loading wallet balances..."}
        </Text>
      </Flex>
    ) : (
      errorMessageContent
    )

  return (
    <>
      <Input
        placeholder={placeholder}
        {...inputStyles}
        {...getInputProps()}
        readOnly={readOnly}
        // If value of input exceeds MAX_SAFE_INTEGER value it will malform the input value.
        // By adding prevent default to 'onBlur' this does not happen
        onBlur={(e) => {
          e.preventDefault()
        }}
      />
      {tokenLabel && (
        <TokenAmountInputTokenLabel
          tokenAmount={tokenAmount}
          tokenLabel={tokenLabel}
          inputProps={inputStyles}
          defaultLabelPosition={defaultLabelPosition}
          textOffset={tokenLabelTextOffset}
          {...tokenLabelProps}
        />
      )}
      {withMaxButton && maxTokenAmount && (
        <TokenAmountInputMaxButton
          setTokenAmount={setTokenAmountCallback}
          maxTokenAmount={maxTokenAmount}
          {...maxButtonProps}
        >
          {maxButtonText}
        </TokenAmountInputMaxButton>
      )}
      {errorMessage && messageContent}
      {!errorMessage && inputDescription}
    </>
  )
}
